import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { getDeclaredFields, getPhoneModels } from '@/features/operations/api';
import { ManualQuotationForm } from '@/features/operations/components/ManualQuotationForm';
import type { PhoneModelOption, PublicDeclaredField } from '@/features/operations/types';

/**
 * Counter intake (#173). Once registered, the request opens in its own detail: that is where
 * the outcome is read out, and where the phone can be received in the same visit.
 */
export function NewQuotationPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [phoneModels, setPhoneModels] = useState<PhoneModelOption[]>([]);
  const [declaredFields, setDeclaredFields] = useState<PublicDeclaredField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        // The catalogue and the questions come from the same public reads the web form uses, so
        // the counter cannot end up offering a different set than the site does.
        const [models, fields] = await Promise.all([getPhoneModels(), getDeclaredFields()]);
        setPhoneModels(models);
        setDeclaredFields(fields);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'No pudimos cargar el formulario.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, []);

  if (!token) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Button component={RouterLink} to="/admin/quotations" color="inherit">
          ← Volver a cotizaciones
        </Button>
      </Box>
      <PageHeader
        eyebrow="COTIZACIONES"
        title="Nueva cotización presencial"
        description="Registrá la cotización de quien está en el local. Sale de los mismos precios que la web."
        badge="Uso interno"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <SectionCard
          title="Datos de la cotización"
          description="Primero el mail, para saber si la persona ya tiene cuenta; después el modelo y el estado declarado del equipo."
        >
          <ManualQuotationForm
            token={token}
            phoneModels={phoneModels}
            declaredFields={declaredFields}
            onCreated={(quotation) =>
              navigate(`/admin/quotations/${quotation.id}`, {
                state: { justCreated: true, invitationSent: quotation.invitationSent },
              })
            }
          />
        </SectionCard>
      )}
    </Stack>
  );
}
